import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import Input from '../components/Input';
import Button from '../components/Button';
import Card from '../components/Card';

// Validation helpers
const validateEmail = (email) => {
  if (!email.trim()) return 'Email is required';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return 'Please provide a valid email address';
  return '';
};

const validatePassword = (password) => {
  if (!password) return 'Password is required';
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (password.length > 128) return 'Password must not exceed 128 characters';
  if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
  if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
  if (!/[@$!%*?&]/.test(password)) return 'Password must contain at least one special character (@$!%*?&)';
  return '';
};

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { forgotPassword, resetPassword } = useAuth();
  const { showToast } = useToast();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleSendOtp = async (e) => {
    e.preventDefault();

    const emailError = validateEmail(email);
    if (emailError) {
      setFieldErrors({ email: emailError });
      return;
    }
    setFieldErrors({});
    setLoading(true);

    const result = await forgotPassword(email);

    if (result.success) {
      showToast(result.message || 'OTP sent to your email', 'success');
      setStep(2);
    } else {
      showToast(result.message, 'error');
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    const errors = {};

    if (!otp.trim()) {
      errors.otp = 'OTP is required';
    } else if (!/^\d{6}$/.test(otp.trim())) {
      errors.otp = 'OTP must be exactly 6 digits';
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) errors.newPassword = passwordError;

    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    setLoading(true);

    const result = await resetPassword(email, otp, newPassword);

    if (result.success) {
      showToast('Password reset successfully. You can now login.', 'success');
      navigate('/login');
    } else {
      showToast(result.message, 'error');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        {/* Logo/Title */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img
              src="/resized-logo.jpg"
              alt="eConfMate Logo"
              className="h-16 w-16 object-contain transition-transform duration-200 hover:scale-110"
            />
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary-600 via-primary-700 to-blue-600 bg-clip-text text-transparent">
              eConfMate
            </h1>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Forgot Password
          </h2>
          <p className="text-gray-600">
            {step === 1 ? 'Enter your email to reset your password' : 'Enter the OTP and your new password'}
          </p>
        </div>

        <Card>
          {step === 1 ? (
            <form onSubmit={handleSendOtp}>
              <Input
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setFieldErrors(prev => ({ ...prev, email: '' })); }}
                placeholder="you@example.com"
                required
                error={fieldErrors.email}
              />

              <Button
                type="submit"
                variant="primary"
                fullWidth
                disabled={loading}
              >
                {loading ? 'Sending OTP...' : 'Send Reset Link'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword}>
              <div className="text-center mb-6">
                <div className="bg-green-100 text-green-700 p-4 rounded-lg mb-4">
                  We've sent a 6-digit OTP to <strong>{email}</strong>.
                </div>
              </div>

              <Input
                label="One-Time Password (OTP)"
                type="text"
                value={otp}
                onChange={(e) => { setOtp(e.target.value); setFieldErrors(prev => ({ ...prev, otp: '' })); }}
                placeholder="Enter 6-digit OTP"
                required
                error={fieldErrors.otp}
              />

              <Input
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setFieldErrors(prev => ({ ...prev, newPassword: '' })); }}
                placeholder="Min 8 chars, upper, lower, number, special"
                required
                error={fieldErrors.newPassword}
              />

              <Input
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setFieldErrors(prev => ({ ...prev, confirmPassword: '' })); }}
                placeholder="Repeat new password"
                required
                error={fieldErrors.confirmPassword}
              />

              {/* Password requirements hint */}
              <div className="mb-4 -mt-2 text-xs text-gray-500">
                <p className="font-medium mb-1">Password must contain:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li className={newPassword.length >= 8 ? 'text-green-600' : ''}>At least 8 characters</li>
                  <li className={/[A-Z]/.test(newPassword) ? 'text-green-600' : ''}>One uppercase letter</li>
                  <li className={/[a-z]/.test(newPassword) ? 'text-green-600' : ''}>One lowercase letter</li>
                  <li className={/[0-9]/.test(newPassword) ? 'text-green-600' : ''}>One number</li>
                  <li className={/[@$!%*?&]/.test(newPassword) ? 'text-green-600' : ''}>One special character (@$!%*?&)</li>
                </ul>
              </div>

              <Button
                type="submit"
                variant="primary"
                fullWidth
                disabled={loading}
              >
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Remember your password?{' '}
              <Link
                to="/login"
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </Card>

        <div className="mt-6 text-center">
          <Link
            to="/"
            className="text-gray-600 hover:text-gray-900 font-medium"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
